/**
 * BE enums mirrored for FE filter pills. Keys are exact slugs from elixaa-core
 * `packages/db/src/schema/practitionerProfiles.ts`. Labels are human-friendly
 * copy used in popover rows and card chips. Update both sides whenever BE adds
 * a value or the project's marketing copy changes.
 */

// ─── Languages ──────────────────────────────────────────────────────────
export const LANGUAGE_KEYS = [
  'english',
  'hindi',
  'tamil',
  'urdu',
  'telugu',
  'malayalam',
  'marathi',
  'saurashtra',
  'punjabi',
  'bengali',
] as const;
export type Language = (typeof LANGUAGE_KEYS)[number];

export const LANGUAGE_LABELS: Record<Language, string> = {
  english: 'English',
  hindi: 'Hindi',
  tamil: 'Tamil',
  urdu: 'Urdu',
  telugu: 'Telugu',
  malayalam: 'Malayalam',
  marathi: 'Marathi',
  saurashtra: 'Saurashtra',
  punjabi: 'Punjabi',
  bengali: 'Bengali',
};

// ─── Specializations (therapy modalities) ───────────────────────────────
export const PRACTITIONER_SPECIALIZATION_KEYS = [
  'cbt',
  'dbt',
  'act',
  'rebt',
  'mbct',
  'mindfulness_based',
  'gestalt',
  'psychodynamic',
  'sfbt',
  'eft',
  'nlp',
  'hypnotherapy',
  'narrative',
  'ta',
  'bsft',
  'trauma_focused',
  'behavioural',
  'supportive',
  'client_centered',
  'eclectic',
  'others',
] as const;
export type PractitionerSpecialization = (typeof PRACTITIONER_SPECIALIZATION_KEYS)[number];

export const SPECIALIZATION_LABELS: Record<PractitionerSpecialization, string> = {
  cbt: 'CBT',
  dbt: 'DBT',
  act: 'ACT',
  rebt: 'REBT',
  mbct: 'MBCT',
  mindfulness_based: 'Mindfulness-Based',
  gestalt: 'Gestalt',
  psychodynamic: 'Psychodynamic',
  sfbt: 'SFBT',
  eft: 'EFT',
  nlp: 'NLP',
  hypnotherapy: 'Hypnotherapy',
  narrative: 'Narrative',
  ta: 'Transactional Analysis',
  bsft: 'BSFT',
  trauma_focused: 'Trauma-Focused',
  behavioural: 'Behavioural',
  supportive: 'Supportive',
  client_centered: 'Client-Centered',
  eclectic: 'Eclectic',
  others: 'Others',
};

// ─── Practitioner concerns (what practitioners help with) ──────────────
// 55 values mirroring BE practitioner_concern pgEnum. Distinct from the
// 11-value session concerns in src/features/sessions/constants/concerns.ts —
// THAT enum is what the client raised in a single session; THIS enum is what
// the practitioner is qualified to address.
export const PRACTITIONER_CONCERN_KEYS = [
  'anxiety_panic',
  'depression_low_mood',
  'postpartum_depression',
  'trauma_ptsd',
  'stress_overwhelm',
  'grief_loss',
  'anger_issues',
  'suicidal_self_harm',
  'emotional_regulation',
  'mood_swings',
  'burnout',
  'sleep_difficulties',
  'phobias_fears',
  'ocd',
  'loneliness_isolation',
  'procrastination',
  'social_anxiety',
  'imposter_syndrome',
  'adhd',
  'romantic_relationship',
  'breakup_divorce_separation',
  'family_conflict',
  'domestic_violence_abuse',
  'communication_difficulties',
  'intimacy_issues',
  'trust_issues',
  'codependency',
  'friendships_social_support',
  'parenting_child_relationship',
  'sibling_rivalry',
  'infidelity',
  'marriage_partnership_challenges',
  'career_job_stress',
  'academic_pressure',
  'financial_stress',
  'major_life_changes',
  'coping_with_disability',
  'self_esteem_confidence',
  'body_image',
  'lgbtq_support',
  'life_purpose_growth',
  'motivation_issues',
  'decision_making',
  'assertiveness_boundaries',
  'existential_spiritual',
  'role_conflicts',
  'substance_use_addiction',
  'internet_gaming_addiction',
  'eating_disorders',
  'sexual_behavior_concerns',
  'gambling',
  'digital_media_overuse',
  'comic_addiction',
  'aging_concern',
  'empty_nest_syndrome',
] as const;
export type PractitionerConcern = (typeof PRACTITIONER_CONCERN_KEYS)[number];

export const CONCERN_LABELS: Record<PractitionerConcern, string> = {
  anxiety_panic: 'Anxiety & Panic',
  depression_low_mood: 'Depression & Low Mood',
  postpartum_depression: 'Postpartum Depression',
  trauma_ptsd: 'Trauma & PTSD',
  stress_overwhelm: 'Stress & Overwhelm',
  grief_loss: 'Grief & Loss',
  anger_issues: 'Anger Issues',
  suicidal_self_harm: 'Suicidal Thoughts & Self-Harm',
  emotional_regulation: 'Emotional Regulation',
  mood_swings: 'Mood Swings',
  burnout: 'Burnout',
  sleep_difficulties: 'Sleep Difficulties',
  phobias_fears: 'Phobias & Fears',
  ocd: 'OCD',
  loneliness_isolation: 'Loneliness & Isolation',
  procrastination: 'Procrastination',
  social_anxiety: 'Social Anxiety',
  imposter_syndrome: 'Imposter Syndrome',
  adhd: 'ADHD',
  romantic_relationship: 'Romantic Relationships',
  breakup_divorce_separation: 'Breakup, Divorce & Separation',
  family_conflict: 'Family Conflict',
  domestic_violence_abuse: 'Domestic Violence & Abuse',
  communication_difficulties: 'Communication Difficulties',
  intimacy_issues: 'Intimacy Issues',
  trust_issues: 'Trust Issues',
  codependency: 'Codependency',
  friendships_social_support: 'Friendships & Social Support',
  parenting_child_relationship: 'Parenting & Child Relationships',
  sibling_rivalry: 'Sibling Rivalry',
  infidelity: 'Infidelity',
  marriage_partnership_challenges: 'Marriage & Partnership Challenges',
  career_job_stress: 'Career & Job Stress',
  academic_pressure: 'Academic Pressure',
  financial_stress: 'Financial Stress',
  major_life_changes: 'Major Life Changes',
  coping_with_disability: 'Coping with Disability',
  self_esteem_confidence: 'Self-Esteem & Confidence',
  body_image: 'Body Image',
  lgbtq_support: 'LGBTQ+ Support',
  life_purpose_growth: 'Life Purpose & Growth',
  motivation_issues: 'Motivation',
  decision_making: 'Decision Making',
  assertiveness_boundaries: 'Assertiveness & Boundaries',
  existential_spiritual: 'Existential & Spiritual',
  role_conflicts: 'Role Conflicts',
  substance_use_addiction: 'Substance Use & Addiction',
  internet_gaming_addiction: 'Internet & Gaming Addiction',
  eating_disorders: 'Eating Disorders',
  sexual_behavior_concerns: 'Sexual Behavior Concerns',
  gambling: 'Gambling',
  digital_media_overuse: 'Digital Media Overuse',
  comic_addiction: 'Comic Addiction',
  aging_concern: 'Aging Concern',
  empty_nest_syndrome: 'Empty Nest Syndrome',
};
